import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { appConfig } from '~/app.config';
import { CtaVariantEnum } from '~/component/call-to-action/enums/variant.enum';
import { CtaWidthEnum } from '~/component/call-to-action/enums/width.enum';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { Activity } from '~/core/model/activity.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';

@Component({
  selector: 'app-organizer-activity-edition-description',
  templateUrl: './description.component.html',
})
export class OrganizerActivityEditionDescriptionComponent
  implements OnInit, OnDestroy
{
  private readonly subscriptions = new Subscription();

  public readonly ctaVariantEnum = CtaVariantEnum;
  public readonly ctaWidthEnum = CtaWidthEnum;

  public readonly minimumCharacters =
    appConfig.formValidationRules.activityDescriptionMinimumLength;
  public readonly variantTips = TipsVariantEnum.INFO;
  public activity: Activity;
  public description: FormControl;
  public isFormValid: boolean;
  public isOverlayDisplayed = false;
  public overlayStatus = SuccessStatusEnum.LOADING;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly locatableService: OrganizerSharedLocatableService,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.description = new FormControl('', [
      Validators.required,
      Validators.minLength(this.minimumCharacters),
      noHtmlTagValidator(),
    ]);

    this.subscriptions.add(
      this.description.valueChanges.subscribe(() => {
        this.isFormValid = this.description.valid && this.description.dirty;
      })
    );

    this.subscriptions.add(
      this.activatedRoute.data.subscribe(({ activityData }) => {
        this.activity = activityData.activity;
        this.description.setValue(this.activity.description);
      })
    );
  }

  public get currentCharacters(): number {
    return this.description.value?.length || 0;
  }

  public get hasInitialValue(): boolean {
    return Boolean(this.activity?.description);
  }

  public updateDescription(): void {
    this.isOverlayDisplayed = true;
    this.organizerSharedActivityService
      .patchActivityById(this.activity.sku, {
        description: [
          this.locatableService.buildCurrentLocatableBody(
            this.description.value
          ),
        ],
      })
      .pipe(
        mergeMap(() =>
          this.organizerSharedActivityService
            .getActivity(this.activity?.sku?.toString())
            .pipe(
              repeatUntil(
                (activity) => activity.description === this.description.value
              )
            )
        )
      )
      .subscribe(() => {
        this.overlayStatus = SuccessStatusEnum.SUCCESS;
      });
  }
}
